import type { PagedListReturn } from '@cks-internal/react-base';
import ProjectStage, { getProjectStageDisplayIndex } from 'src/enums/companyProjects/ProjectStage';
import type {
  MyAssignedProjectContainersResultModel,
  ProjectContainerCompanyGroupModel,
  ProjectContainerListItemModel,
  ProjectStageGroupModel
} from 'src/features/companyProjects/navigation/types/MyAssignedProjectContainersModels';
import type { ProjectContainerTableModel } from 'src/features/companyProjects/projectContainers/manageContainers/types/ProjectContainerTableModel';
import type { ProjectContainerListEvent } from '../events/projectContainerEventTypes';

const mapStageStringToEnum = (stage: string | undefined): ProjectStage | undefined => {
  if (!stage) return undefined;
  switch (stage) {
    case 'Assessment':
      return ProjectStage.Assessment;
    case 'Sourcing':
      return ProjectStage.Sourcing;
    case 'Ongoing':
      return ProjectStage.Ongoing;
    case 'Cancelled':
      return ProjectStage.Cancelled;
    case 'Completed':
      return ProjectStage.Completed;
    case 'Archived':
      return ProjectStage.Archived;
    case 'Bid':
      return ProjectStage.Bid;
    default:
      return undefined;
  }
};

const cloneStageGroups = (groups: ProjectStageGroupModel[]): ProjectStageGroupModel[] =>
  groups.map((group) => ({
    ...group,
    Companies: cloneAssignedGroups(group.Companies)
  }));

const findStageGroup = (
  groups: ProjectStageGroupModel[],
  containerId: number
): { stageIndex: number; groupIndex: number; containerIndex: number } | null => {
  for (let si = 0; si < groups.length; si += 1) {
    const loc = findAssignedContainer(groups[si].Companies, containerId);
    if (loc) return { stageIndex: si, ...loc };
  }
  return null;
};

const cloneAssignedGroups = (groups: ProjectContainerCompanyGroupModel[]): ProjectContainerCompanyGroupModel[] =>
  groups.map((group) => ({
    ...group,
    Containers: group.Containers.map<ProjectContainerListItemModel>((c) => ({ ...c }))
  }));

const findAssignedContainer = (
  groups: ProjectContainerCompanyGroupModel[],
  containerId: number
): { groupIndex: number; containerIndex: number } | null => {
  for (let gi = 0; gi < groups.length; gi += 1) {
    const containers = groups[gi].Containers;
    const ci = containers.findIndex((c) => c.Id === containerId);
    if (ci !== -1) {
      return { groupIndex: gi, containerIndex: ci };
    }
  }
  return null;
};

const ensureAssignedGroup = (
  groups: ProjectContainerCompanyGroupModel[],
  companyId: number
): { groups: ProjectContainerCompanyGroupModel[]; groupIndex: number } => {
  const existingIndex = groups.findIndex((g) => g.CompanyId === companyId);
  if (existingIndex !== -1) {
    return { groups, groupIndex: existingIndex };
  }

  const newGroup: ProjectContainerCompanyGroupModel = {
    CompanyId: companyId,
    CompanyName: '',
    Containers: []
  };

  return {
    groups: [...groups, newGroup],
    groupIndex: groups.length
  };
};

const insertStageGroupInDisplayOrder = (stageGroups: ProjectStageGroupModel[], stage: ProjectStage): number => {
  const newIndex = getProjectStageDisplayIndex(stage);
  const insertAt = stageGroups.findIndex((g) => getProjectStageDisplayIndex(g.Stage) > newIndex);
  const targetStageIndex = insertAt === -1 ? stageGroups.length : insertAt;
  stageGroups.splice(targetStageIndex, 0, { Stage: stage, Companies: [] });
  return targetStageIndex;
};

export const applyProjectContainerListEventToAssignedContainersState = (
  current: MyAssignedProjectContainersResultModel | undefined,
  event: ProjectContainerListEvent,
  currentUserId?: string
): MyAssignedProjectContainersResultModel | undefined => {
  if (!current) return current;

  const stageEnum = mapStageStringToEnum(event.stage);
  if (!stageEnum) return current;

  const stageGroups = cloneStageGroups(current.StageGroups ?? []);
  const existingLoc = findStageGroup(stageGroups, event.containerId);
  const isAssignedToCurrentUser =
    !currentUserId ||
    event.assignedToUserId === currentUserId ||
    (event.additionalAssignedToUserIds ?? []).includes(currentUserId);

  const removeExisting = () => {
    if (!existingLoc) return;
    const stageGroup = stageGroups[existingLoc.stageIndex];
    const group = stageGroup.Companies[existingLoc.groupIndex];
    const updatedGroup: ProjectContainerCompanyGroupModel = {
      ...group,
      Containers: group.Containers.filter((_, idx) => idx !== existingLoc.containerIndex)
    };
    stageGroups[existingLoc.stageIndex] = {
      ...stageGroup,
      Companies: stageGroup.Companies.map((g, idx) => (idx === existingLoc.groupIndex ? updatedGroup : g))
    };
  };

  // If we know the current user and this event is for a different assignment set,
  // remove the container from "my assigned" lists.
  if (!isAssignedToCurrentUser) {
    if (!existingLoc) return current;
    removeExisting();

    return {
      ...current,
      StageGroups: stageGroups
    };
  }

  let existingItem: ProjectContainerListItemModel | undefined;
  if (existingLoc) {
    existingItem =
      stageGroups[existingLoc.stageIndex].Companies[existingLoc.groupIndex].Containers[existingLoc.containerIndex];
    removeExisting();
  }

  const baseItem: ProjectContainerListItemModel = existingItem ?? {
    Id: event.containerId,
    Name: event.name,
    Stage: stageEnum,
    CreatedOn: new Date().toISOString(),
    UpdatedOn: null,
    UnreadCount: 0
  };

  const updatedItem: ProjectContainerListItemModel = {
    ...baseItem,
    Name: event.name,
    Stage: stageEnum,
    UpdatedOn: new Date().toISOString()
  };

  let targetStageIndex = stageGroups.findIndex((g) => g.Stage === stageEnum);
  if (targetStageIndex === -1) {
    targetStageIndex = insertStageGroupInDisplayOrder(stageGroups, stageEnum);
  }

  const { groups: withGroup, groupIndex } = ensureAssignedGroup(
    stageGroups[targetStageIndex].Companies,
    event.companyId
  );
  const targetGroup = withGroup[groupIndex];

  const withoutDuplicate = targetGroup.Containers.filter((c) => c.Id !== event.containerId);
  const updatedGroup: ProjectContainerCompanyGroupModel = {
    ...targetGroup,
    Containers: [...withoutDuplicate, updatedItem]
  };

  const nextGroups = [...withGroup];
  nextGroups[groupIndex] = updatedGroup;

  stageGroups[targetStageIndex] = {
    ...stageGroups[targetStageIndex],
    Companies: nextGroups
  };

  return {
    ...current,
    StageGroups: stageGroups
  };
};

export const applyProjectContainerListEventToTablePage = (
  current: PagedListReturn<ProjectContainerTableModel> | undefined,
  event: ProjectContainerListEvent
): PagedListReturn<ProjectContainerTableModel> | undefined => {
  if (!current) return current;

  const stageEnum = mapStageStringToEnum(event.stage);

  const rows = current.PagedRows ?? [];
  const index = rows.findIndex((r) => r.Id === event.containerId);

  if (event.type === 'Updated') {
    if (index === -1) return current;
    const updatedRows = rows.map((row) =>
      row.Id === event.containerId
        ? {
            ...row,
            Name: event.name,
            Stage: stageEnum ?? row.Stage
          }
        : row
    );
    return { ...current, PagedRows: updatedRows };
  }

  if (event.type === 'Created') {
    if (index !== -1) {
      // Treat as update if it already exists
      const updatedRows = rows.map((row) =>
        row.Id === event.containerId
          ? {
              ...row,
              Name: event.name,
              Stage: stageEnum ?? row.Stage
            }
          : row
      );
      return { ...current, PagedRows: updatedRows };
    }

    const newRow: ProjectContainerTableModel = {
      Id: event.containerId,
      Name: event.name,
      CompanyId: event.companyId,
      CompanyName: rows[0]?.CompanyName ?? '',
      Stage: stageEnum ?? rows[0]?.Stage ?? ProjectStage.Assessment,
      AssignedToUserName: null,
      AdditionalAssignedToUserNames: [],
      CityNames: [],
      HotelNames: []
    };

    return {
      ...current,
      TotalRows: (current.TotalRows ?? rows.length) + 1,
      PagedRows: [newRow, ...rows]
    };
  }

  return current;
};
